/**
 * Q7 박제 옵션 C — 매장 영업 상태 라벨 매트릭스 (menu#347 / admin#1230 / admin#1298)
 *
 * BE `StoreResource` 의 4 필드 (`is_open_now`, `status`, `closure_reason`,
 * `next_available_at`) 를 입력으로 받아 사용자에게 보여줄 단일 라벨 문자열을
 * 반환합니다. FE 는 raw `operating_hours` 를 재해석하지 않습니다 (Q7 박제).
 *
 * 라벨 매트릭스:
 *  - `is_open_now=true`  + `status=temporarily_open` → "관리자 임시 오픈 (운영시간 외)"
 *  - `is_open_now=false` + `status=approved` + `closure_reason=outside_hours`
 *      → "오늘 영업시간 외 — 다음 영업 HH:MM"
 *  - `is_open_now=false` + `status=temporarily_closed` → "관리자 임시 휴무"
 *  - 그 외 → 기본 라벨 ("영업 중" / "영업종료")
 *
 * @see olulo-mx-admin/app/Http/Resources/StoreResource.php
 * @see olulo-mx-admin/app/Services/StoreHoursService.php
 * @see menu#327 (모순 케이스 / 본 PR 의 회귀 게이트)
 */
import type { Locale } from '@/config/locales';
import type { EntityStatus, StoreClosureReason } from '@/lib/api/types';

/** 라벨 매트릭스 입력 (BE 응답 4 필드) */
export interface StoreOpenLabelInput {
  /** BE `is_open_now` */
  isOpenNow: boolean;
  /** BE `status` */
  status: EntityStatus;
  /** BE `closure_reason` (hours 미로드 시 undefined) */
  closureReason?: StoreClosureReason | null;
  /** BE `next_available_at` ISO8601 (store timezone). null 가능. */
  nextAvailableAt?: string | null;
  /** 매장 IANA 타임존 (예: 'America/Mexico_City'). HH:MM 표기에 사용. */
  storeTimezone?: string | null;
}

/** 라벨 매트릭스 출력 */
export interface StoreOpenLabel {
  /** 사용자 표시 라벨 */
  text: string;
  /** UI 강조용 시맨틱 키 — primary(영업 중) / warning(주의) / muted(영업종료) */
  tone: 'primary' | 'warning' | 'muted';
}

interface LocalizedTexts {
  open: string;
  closed: string;
  temporarilyOpen: string;
  outsideHoursWithNext: (next: string) => string;
  outsideHoursNoNext: string;
  temporarilyClosed: string;
  suspended: string;
}

/**
 * 라벨 텍스트 — Q1 옵션 A 박제 (menu#366):
 *
 * 동작 의도를 명시적으로 표현하여 사용자가 "주문/픽업 가능 여부" 를 즉시
 * 인식할 수 있도록 명료화합니다. 좁은 카드 영역에서 inline 배지 / 오버레이
 * 로 표시되므로 *짧고 동작 지향* 텍스트를 우선합니다.
 *
 * - `temporarilyOpen` — warning 배지 (영업 가능). "지금 픽업 가능" 강조.
 * - `outsideHours*` — muted 오버레이 (영업 불가). "오늘 마감" + 다음 영업 시각.
 * - `temporarilyClosed` — muted 오버레이. "일시 운영 중단" (관리자 수동).
 * - `suspended` — muted. "운영 정지" (영구/장기).
 */
const TEXTS = {
  ko: {
    open: '영업 중',
    closed: '영업 종료',
    temporarilyOpen: '지금 픽업 가능 (임시 오픈)',
    outsideHoursWithNext: (next) => `오늘 마감 — 다음 영업 ${next}`,
    outsideHoursNoNext: '오늘 마감 — 픽업 불가',
    temporarilyClosed: '일시 운영 중단',
    suspended: '운영 정지',
  },
  es: {
    open: 'Abierto',
    closed: 'Cerrado',
    temporarilyOpen: 'Disponible ahora (apertura temporal)',
    outsideHoursWithNext: (next) => `Cerrado hoy — abre ${next}`,
    outsideHoursNoNext: 'Cerrado hoy — sin pedidos para recoger',
    temporarilyClosed: 'Suspensión temporal',
    suspended: 'Servicio suspendido',
  },
  en: {
    open: 'Open',
    closed: 'Closed',
    temporarilyOpen: 'Available now (temporary opening)',
    outsideHoursWithNext: (next) => `Closed today — opens ${next}`,
    outsideHoursNoNext: 'Closed today — no pickup',
    temporarilyClosed: 'Temporarily suspended',
    suspended: 'Service suspended',
  },
  pt: {
    open: 'Aberto',
    closed: 'Fechado',
    temporarilyOpen: 'Disponível agora (abertura temporária)',
    outsideHoursWithNext: (next) => `Fechado hoje — abre ${next}`,
    outsideHoursNoNext: 'Fechado hoje — sem retirada',
    temporarilyClosed: 'Operação temporariamente suspensa',
    suspended: 'Serviço suspenso',
  },
  fr: {
    open: 'Ouvert',
    closed: 'Fermé',
    temporarilyOpen: 'Disponible maintenant (ouverture temporaire)',
    outsideHoursWithNext: (next) => `Fermé aujourd'hui — ouvre à ${next}`,
    outsideHoursNoNext: 'Fermé aujourd\'hui — retrait indisponible',
    temporarilyClosed: 'Service temporairement suspendu',
    suspended: 'Service suspendu',
  },
  zh: {
    open: '营业中',
    closed: '已关闭',
    temporarilyOpen: '现在可自取（临时营业）',
    outsideHoursWithNext: (next) => `今日已打烊 — 下次营业 ${next}`,
    outsideHoursNoNext: '今日已打烊 — 暂不可自取',
    temporarilyClosed: '临时暂停营业',
    suspended: '服务已暂停',
  },
  ja: {
    open: '営業中',
    closed: '営業終了',
    temporarilyOpen: '現在受け取り可能（一時営業）',
    outsideHoursWithNext: (next) => `本日は終了 — 次回営業 ${next}`,
    outsideHoursNoNext: '本日は終了 — 受け取り不可',
    temporarilyClosed: '一時営業停止',
    suspended: 'サービス停止中',
  },
  hi: {
    open: 'खुला',
    closed: 'बंद',
    temporarilyOpen: 'अभी पिकअप उपलब्ध (अस्थायी खुला)',
    outsideHoursWithNext: (next) => `आज बंद — अगला समय ${next}`,
    outsideHoursNoNext: 'आज बंद — पिकअप उपलब्ध नहीं',
    temporarilyClosed: 'अस्थायी रूप से संचालन बंद',
    suspended: 'सेवा बंद',
  },
  de: {
    open: 'Geöffnet',
    closed: 'Geschlossen',
    temporarilyOpen: 'Jetzt verfügbar (vorübergehend geöffnet)',
    outsideHoursWithNext: (next) => `Heute geschlossen — öffnet ${next}`,
    outsideHoursNoNext: 'Heute geschlossen — keine Abholung',
    temporarilyClosed: 'Vorübergehend ausgesetzt',
    suspended: 'Dienst ausgesetzt',
  },
  it: {
    open: 'Aperto',
    closed: 'Chiuso',
    temporarilyOpen: 'Disponibile ora (apertura temporanea)',
    outsideHoursWithNext: (next) => `Chiuso oggi — apre ${next}`,
    outsideHoursNoNext: 'Chiuso oggi — ritiro non disponibile',
    temporarilyClosed: 'Servizio temporaneamente sospeso',
    suspended: 'Servizio sospeso',
  },
  ru: {
    open: 'Открыто',
    closed: 'Закрыто',
    temporarilyOpen: 'Доступно сейчас (временное открытие)',
    outsideHoursWithNext: (next) => `Закрыто сегодня — откроется ${next}`,
    outsideHoursNoNext: 'Закрыто сегодня — самовывоз недоступен',
    temporarilyClosed: 'Временно приостановлено',
    suspended: 'Обслуживание приостановлено',
  },
  ar: {
    open: 'مفتوح',
    closed: 'مغلق',
    temporarilyOpen: 'متاح الآن (فتح مؤقت)',
    outsideHoursWithNext: (next) => `مغلق اليوم — يفتح ${next}`,
    outsideHoursNoNext: 'مغلق اليوم — لا يوجد استلام',
    temporarilyClosed: 'التشغيل متوقف مؤقتًا',
    suspended: 'الخدمة متوقفة',
  },
  vi: {
    open: 'Đang mở',
    closed: 'Đã đóng',
    temporarilyOpen: 'Có thể nhận ngay (mở tạm thời)',
    outsideHoursWithNext: (next) => `Hôm nay đã đóng — mở lúc ${next}`,
    outsideHoursNoNext: 'Hôm nay đã đóng — không nhận hàng',
    temporarilyClosed: 'Tạm ngừng hoạt động',
    suspended: 'Dịch vụ đã tạm dừng',
  },
  th: {
    open: 'เปิดอยู่',
    closed: 'ปิด',
    temporarilyOpen: 'รับได้ตอนนี้ (เปิดชั่วคราว)',
    outsideHoursWithNext: (next) => `วันนี้ปิดแล้ว — เปิด ${next}`,
    outsideHoursNoNext: 'วันนี้ปิดแล้ว — ไม่รับออร์เดอร์',
    temporarilyClosed: 'หยุดให้บริการชั่วคราว',
    suspended: 'ระงับบริการ',
  },
  nl: {
    open: 'Open',
    closed: 'Gesloten',
    temporarilyOpen: 'Nu beschikbaar (tijdelijk open)',
    outsideHoursWithNext: (next) => `Vandaag gesloten — opent ${next}`,
    outsideHoursNoNext: 'Vandaag gesloten — geen afhalen',
    temporarilyClosed: 'Tijdelijk onderbroken',
    suspended: 'Service onderbroken',
  },
} satisfies Record<Locale, LocalizedTexts>;

const INTL_LOCALES = {
  ko: 'ko-KR',
  es: 'es-MX',
  en: 'en-US',
  pt: 'pt-BR',
  fr: 'fr-FR',
  zh: 'zh-CN',
  ja: 'ja-JP',
  hi: 'hi-IN',
  de: 'de-DE',
  it: 'it-IT',
  ru: 'ru-RU',
  ar: 'ar-SA',
  vi: 'vi-VN',
  th: 'th-TH',
  nl: 'nl-NL',
} satisfies Record<Locale, string>;

/**
 * ISO8601 → 매장 타임존 기준 'HH:MM' 표기.
 * 파싱 실패 시 빈 문자열 반환 → 호출부는 outsideHoursNoNext 사용.
 */
export function formatNextAvailableTime(
  iso: string | null | undefined,
  storeTimezone: string | null | undefined,
  language: Locale,
): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const intlLocale = INTL_LOCALES[language];

  try {
    return new Intl.DateTimeFormat(intlLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: language === 'en',
      timeZone: storeTimezone || undefined,
    }).format(date);
  } catch (err) {
    // #490 시정: RangeError (invalid IANA timezone) 만 fallback. 다른 예외는
    // rethrow 하여 silent failure 차단 (박제 silent_pass_recurrence 정합).
    if (!(err instanceof RangeError)) {
      throw err;
    }
    // 잘못된 IANA timezone — 브라우저 timezone fallback (timeZone 옵션 미지정)
    return new Intl.DateTimeFormat(intlLocale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: language === 'en',
    }).format(date);
  }
}

/**
 * BE 4 필드 → 사용자 라벨 변환 (Q7 매트릭스).
 *
 * 우선순위 (BE 와 정합):
 *  1. `temporarily_closed` → 임시 휴무
 *  2. `suspended` → 운영 정지
 *  3. `is_open_now=true` + `temporarily_open` → 임시 오픈
 *  4. `is_open_now=false` + `approved` + `outside_hours` → 영업시간 외
 *  5. `is_open_now=true` → 영업 중 (기본)
 *  6. 그 외 → 영업종료
 */
export function getStoreOpenLabel(
  input: StoreOpenLabelInput,
  language: Locale,
): StoreOpenLabel {
  const texts = TEXTS[language];
  const { isOpenNow, status, closureReason, nextAvailableAt, storeTimezone } = input;

  if (status === 'temporarily_closed') {
    return { text: texts.temporarilyClosed, tone: 'muted' };
  }
  if (status === 'suspended') {
    return { text: texts.suspended, tone: 'muted' };
  }
  if (isOpenNow && status === 'temporarily_open') {
    return { text: texts.temporarilyOpen, tone: 'warning' };
  }
  if (!isOpenNow && status === 'approved' && closureReason === 'outside_hours') {
    const formatted = formatNextAvailableTime(nextAvailableAt, storeTimezone, language);
    return {
      text: formatted ? texts.outsideHoursWithNext(formatted) : texts.outsideHoursNoNext,
      tone: 'muted',
    };
  }
  if (isOpenNow) {
    return { text: texts.open, tone: 'primary' };
  }
  return { text: texts.closed, tone: 'muted' };
}
